import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useSetAtom } from 'jotai';
import { memo, useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button, toast } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import { Option } from '@/lib/v2/components/Select/Select';
import { UploadZone } from '@/lib/v2/examples';

import {
  IMappingField,
  wrappingMappingData,
} from '@/src/ContactsModule/components/ContactsMappingField/ContactsMappingField';
import { validateStateFile } from '@/src/ContactsModule/utils/enums/ValidateStateFile.Enum';
import { validateFile } from '@/src/ContactsModule/utils/File';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { IEmblueService } from '@/src/infrastructure/Protocol/IEmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';
import { useUserData } from '@/src/infrastructure/services/useUserData';
import {
  atomFirstStepInternal,
  atomFirstStepUploadPercentage,
  atomReadyToStartImport,
} from '@/src/modules/ContactsModule/atoms/Step1';
import { UploadProgress } from '@/src/modules/ContactsModule/screens/ImportMain/components/UploadProgress';
import { ImportSteps } from '@/src/modules/ContactsModule/screens/ImportMain/ImportStepEnum.enum';
import { CustomsFieldsProps } from '@/src/modules/ContactsModule/screens/ImportMain/V2ImportTypes';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';

import {
  atomFileDataQuantity,
  atomListCustomField,
  atomMappingList,
} from '@/modules/ContactsModule/atoms/Step2';
import {
  atomDelimiter,
  atomFileName,
  atomFiles,
  atomShowProgressNotification,
} from '@/modules/ContactsModule/atoms/Steps';
import { DELIMITER_LIST } from '@/modules/ContactsModule/constants';
import { useResetFile } from '@/modules/ContactsModule/hooks/useResetFile';
import { useResetImport } from '@/modules/ContactsModule/hooks/useResetImport';

const dropConfig = {
  noClick: false,
  maxFiles: 1,
  maxSize: 100 * 1024 * 1024, // 100 MB * 1024 => KB *1024 => bytes
  accept: 'text/csv',
  multiple: false,
};

interface IStep1Props {
  noImport: {
    id: number;
    name: string;
    value: string;
  };
}

const Step1 = (props: IStep1Props) => {
  const { noImport } = props;
  const [internalStep, setInternalStep] = useAtom(atomFirstStepInternal);
  const [uploadPercentage, setUploadPercentage] = useAtom(atomFirstStepUploadPercentage);
  const [fileName, setFileName] = useAtom(atomFileName);
  const [files, setFiles] = useAtom(atomFiles);
  const [readyToStartImport, setReadyToStartImport] = useAtom(atomReadyToStartImport);
  const setMappingList = useSetAtom(atomMappingList);
  const setFileDataQuantity = useSetAtom(atomFileDataQuantity);
  const { t } = useTranslation();
  const { resetImport } = useResetImport();
  const setDelimiter = useSetAtom(atomDelimiter);
  const setSelectListCustomField = useSetAtom(atomListCustomField);
  const setShowProgressNotification = useSetAtom(atomShowProgressNotification);

  const [isDraged, setIsDraged] = useState(false);
  const service = useService() as IEmblueService;
  const { resetStep1Loader } = useResetFile();
  const [userData] = useUserData();
  const [listCustomField] = useEmblue(ServiceMethods.getListCustomFields);

  const onDragEnter = () => {
    setIsDraged(true);
  };

  const onDragLeave = () => {
    setIsDraged(false);
  };

  const onDrop = async <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
    setIsDraged(false);
    setFileName('');
    const acceptFile = acceptedFiles.at(0);
    let errorType = 'unknown';
    try {
      if (acceptFile) {
        await validateFile(acceptFile);
        setFiles({ ...files, file: acceptFile, step: ImportSteps.import });
        setFileName(acceptFile.name);
        setReadyToStartImport(true);
        datadogRum.addAction('ContactsImport.onDropFileSuccess', {
          email: userData.email,
          accountId: Number(userData.companyId),
        });
        return;
      } else if (fileRejections.length) {
        fileRejections.at(0)?.errors.forEach((element: { code: string; message: string }) => {
          if (element.code === 'file-invalid-type') {
            toast({
              id: 'invalid-file-notification',
              title: t('IMPORT_MAIN.invalidFile'),
              variant: 'error',
              body: t('IMPORT_MAIN.invalidFileBody'),
            });
          } else if (element.code === 'file-too-large') {
            toast({
              id: 'invalid-file-notification',
              title: t('IMPORT_MAIN.invalidFile'),
              variant: 'error',
              body: t('IMPORT_MAIN.overSizeFile'),
            });
          }

          errorType = element.code;
        });
        setFiles({ ...files, fileRejections: fileRejections.at(0) });
      }
    } catch (error) {
      console.error(error);
      if (error === validateStateFile.CharacterNotAllowed) {
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          variant: 'error',
          body: t('IMPORT_MAIN.characterNotAllowed'),
        });
        errorType = validateStateFile.CharacterNotAllowed;
      } else if (error === validateStateFile.fileEmpty) {
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          variant: 'error',
          body: t('IMPORT_MAIN.fileEmpty'),
        });
        errorType = validateStateFile.fileEmpty;
      }
    }
    datadogRum.addAction('ContactsImport.onDropFileError', {
      email: userData.email,
      accountId: Number(userData.companyId),
      errorType,
    });
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    ...dropConfig,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onDrop,
    onDragEnter,
    onDragLeave,
  });

  const handleUploadProgress = useCallback(
    (percentCompleted: number) => {
      setUploadPercentage(percentCompleted);
      setShowProgressNotification((prevProgressNotification) => ({
        ...prevProgressNotification,
        percentage: percentCompleted,
        status: percentCompleted === 100 ? loadingStatus.ready : loadingStatus.loading,
        description: t('IMPORT_MAIN.uploadingFile') ?? '',
        show: localStorage.getItem('@import/outside') === 'true',
      }));
    },
    [setShowProgressNotification, setUploadPercentage, t]
  );

  const importConfigurationProcess = useCallback(
    async (fileName: string) => {
      if (!fileName) return;
      setInternalStep('mappingFields');
      const { success, data } = await service.getImportConfiguration({
        fileName: fileName,
      });

      resetStep1Loader();

      if (!success || !data.onSuccess) {
        if (data.onError?.parseError) {
          const { LineError, Delimiter } = data.onError.parseError;
          toast({
            id: 'invalid-file-notification',
            title: t('IMPORT_MAIN.invalidFile'),
            body: t('IMPORT_MAIN.parseError', { lineError: LineError, delimiter: Delimiter }),
            variant: 'error',
            autoCloseDelay: 10000,
          });
        } else {
          toast({
            id: 'mapping-fields-notification-error',
            title: t('MAPPING_NOTIFICATION.errorTitle'),
            body: t('MAPPING_NOTIFICATION.errorBody'),
            variant: 'error',
          });
        }
        return;
      }

      const configColumn = data.onSuccess;
      const { quantityColumns, quantityContacts, headers } = configColumn;

      if (quantityContacts === 0) {
        toast({
          id: 'import-files-notification-error',
          title: t('MAPPING_NOTIFICATION.errorTitle'),
          body: t('IMPORT_MAIN.fileEmpty'),
          variant: 'error',
        });
        return;
      }
      if (configColumn && listCustomField) {
        const suggestedDelimiter = DELIMITER_LIST.find(
          (delimited: Option) => configColumn?.suggestedDelimiter === delimited.value
        );

        if (suggestedDelimiter) {
          setDelimiter(suggestedDelimiter);
        }

        const mappingField: IMappingField = wrappingMappingData(listCustomField, configColumn);
        // cspell:disable
        const disabledFieldNames = [
          'nps',
          'recency',
          'frequency',
          'amount',
          'eCommerce Classification',
          'fecha_creacion',
          'ultima_apertura',
          'ultimo_viral',
          'ultimo_click',
          'ultimo_viral',
          'ultima_actividad',
          'ultima_visita_web',
          'ultimo_tag_web',
          'ultimo_item_web',
          'ultima_suscripcion_push',
          'ultimo_envio_push',
          'ultimo_envio_sms',
        ];
        // cspell:enable

        const importArray = [
          noImport,
          ...listCustomField
            .filter((item) => {
              return !disabledFieldNames.includes(item.name);
            })
            .map((element: CustomsFieldsProps) => {
              return { id: element.id, name: element.name, value: element.name };
            }),
        ];
        setMappingList({ mappingList: [...mappingField.mappingList] });
        setFileDataQuantity({ quantityContacts, quantityColumns });
        setSelectListCustomField(importArray);

        setFiles((prevFiles) => ({
          ...prevFiles,
          step: ImportSteps.mappingField,
          selectListCustomField: importArray,
          quantityColumns,
          quantityContacts,
          headers,
        }));
      }
    },
    [
      setInternalStep,
      service,
      resetStep1Loader,
      listCustomField,
      t,
      noImport,
      setMappingList,
      setFileDataQuantity,
      setSelectListCustomField,
      setFiles,
      setDelimiter,
    ]
  );
  const importFile = useCallback(async () => {
    const file = files?.file;

    if (fileName && !file) {
      // recentImportTable case
      await importConfigurationProcess(fileName);
      return;
    }

    if (!file) return;

    const formData = new FormData();
    formData.append('Filedata', file);

    if (!files?.mappingList) {
      setReadyToStartImport(false);
      setInternalStep('uploadingFile');
      const uploadStatus = await service.fileUploader({ formData }, handleUploadProgress);

      if (uploadStatus.success) {
        await importConfigurationProcess(file.name);
        return;
      } else if (uploadStatus.data.fileTooLarge) {
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          variant: 'error',
          body: t('IMPORT_MAIN.overSizeFile'),
        });
      } else {
        toast({
          id: 'mapping-fields-notification-error',
          title: t('MAPPING_NOTIFICATION.errorTitle'),
          body: t('MAPPING_NOTIFICATION.errorBody'),
          variant: 'error',
        });
      }
      resetImport();
    }
  }, [
    fileName,
    files?.file,
    files?.mappingList,
    handleUploadProgress,
    importConfigurationProcess,
    resetImport,
    setInternalStep,
    setReadyToStartImport,
    service,
    t,
  ]);

  if (files.step !== ImportSteps.import) return null;

  return (
    <>
      <div className="px-10 pb-0 pt-6">
        {internalStep === 'readyToSelectFile' && (
          <UploadZone
            fileName={fileName}
            inputProps={getInputProps}
            isDraged={isDraged}
            rootProps={getRootProps({ className: 'dropzone' })}
            onClickHandler={open}
          />
        )}

        {internalStep !== 'readyToSelectFile' && (
          <UploadProgress
            isLoadingMapFields={internalStep === 'mappingFields'}
            isLoadingUploadFile={internalStep === 'uploadingFile'}
            // isMappingFieldsCompleted={internalStep === 'mappingFieldsEnd'}
            percentage={uploadPercentage}
            text={t(`IMPORT_MAIN.${internalStep}`)}
            // onCompletionAnimationEnd={resetStep1Loader}
            // onUploadFileCompleted={() => {
            //   setInternalStep('mappingFields');
            //   simulateMappingAwait();
            // }}
          />
        )}
      </div>
      <Flex column>
        <Flex alignment="end">
          <div className="px-10 py-6 text-14">{t('IMPORT_MAIN.consent')}</div>
        </Flex>
        <Flex alignment="end" className="border-t border-emblue-borders px-10 py-2">
          <Button
            disabled={!readyToStartImport}
            id="import-continue"
            onClick={() => void importFile()}
          >
            {t('IMPORT_MAIN.importContacts')}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default memo(Step1);
